import { updateMainIndustryApi, useGetMainIndustryList } from "@/api/services/global/master";
import { TGetMainIndustryListOutputDto } from "@/api/services/global/master/dtos/get-master-main-industry-list.dto";
import { IconOrderTable } from "@/assets/icons/IconOrderTable";
import { ConfirmLeavePage } from "@/components/commons/ConfirmLeavePage";
import { Button } from "@/components/elements/Button";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_TIME_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { HiddenMainIndustryModal } from "@/features/master-data/components/MainIndustry/HiddenMainIndustryModal";
import { MainIndustryFormModal } from "@/features/master-data/components/MainIndustry/MainIndustryFormModal";
import { MASTER_DATA_TYPE_TITLE } from "@/features/master-data/constants/masterDataType";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { background, blue, button, divider, green, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { Box, Stack, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";

export const MasterDataMainIndustry = () => {
  const formModal = useVisible();
  const [hiddenMainIndustry, setHiddenMainIndustry] = useState<TGetMainIndustryListOutputDto>();
  const [selectedMainIndustry, setSelectedMainIndustry] = useState<TGetMainIndustryListOutputDto>();
  const [enableSaveOrderBtn, setEnableOrderBtn] = useState<boolean>(false);
  const { data, isLoading } = useGetMainIndustryList();

  const [rows, setRows] = useState<TGetMainIndustryListOutputDto[]>(data);

  useEffect(() => {
    if (data && data.length > 0) setRows(data);
  }, [data]);

  const handleSaveOrder = async () => {
    try {
      const rs = await updateMainIndustryApi({
        mainIndustries: rows.map((item) => ({
          id: toNumber(item.id),
          mainIndustry: item.mainIndustry,
          gzMainIndustryRenewVisaCsvCode: item.gzMainIndustryRenewVisaCsvCode,
        })),
      });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setEnableOrderBtn(false);
    } catch (error) {
      showError(error);
    }
  };

  // Handle row dragging
  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !result.source || result.source.index === result.destination.index) return;
    const reorderedRows = Array.from(rows);
    const [removed] = reorderedRows.splice(result.source.index, 1);
    reorderedRows.splice(result.destination.index, 0, removed);
    setRows(reorderedRows);
    setEnableOrderBtn(true);
  };

  if (isLoading || rows.length === 0) return <LoadingPage />;

  return (
    <>
      <TopPage title={"「" + MASTER_DATA_TYPE_TITLE.MAIN_INDUSTRY + "」の編集"} backUrl={AppRoutes.masterData} />
      <Stack gap={3}>
        <Stack direction="row" justifyContent="flex-end" gap={3}>
          <Button size="sm" variant="outline" onClick={() => handleSaveOrder()} disabled={!enableSaveOrderBtn}>
            オーダーを保存する
          </Button>
          <Button size="sm" variant="outline" onClick={() => formModal.open()}>
            項目追加
          </Button>
        </Stack>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="tableRows" direction="vertical">
            {(provided) => (
              <StyledTable {...provided.droppableProps} ref={provided.innerRef}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>主たる業種</TableCell>
                      <TableCell>期間更新CSVのコード（列GZ）</TableCell>
                      <TableCell>最終更新日</TableCell>
                      <TableCell>有効／無効</TableCell>
                      <TableCell>編集</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <Draggable key={row.id} draggableId={row.id.toString()} index={index}>
                        {(provided) => (
                          <TableRow ref={provided.innerRef} {...provided.draggableProps}>
                            <TableCell sx={{ width: "3%" }} {...provided.dragHandleProps}>
                              <IconOrderTable color={blue[80]} />
                            </TableCell>
                            <TableCell sx={{ width: "37%" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                {row.mainIndustry || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: "24%" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                {row.gzMainIndustryRenewVisaCsvCode || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: "20%" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} sx={{ px: 0.5 }} variant="body14">
                                {row.updatedAt ? dayjs(row.updatedAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: "8%" }}>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setHiddenMainIndustry(row);
                                }}
                              >
                                {row.isHidden ? "有効化" : "無効化"}
                              </Button>
                            </TableCell>
                            <TableCell sx={{ width: "8%" }}>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedMainIndustry(row);
                                  formModal.open();
                                }}
                              >
                                編集
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              </StyledTable>
            )}
          </Droppable>
        </DragDropContext>
      </Stack>

      {formModal.visible && (
        <MainIndustryFormModal
          onClose={() => {
            formModal.close();
            setSelectedMainIndustry(undefined);
          }}
          isOpen={formModal.visible}
          selectedMainIndustry={selectedMainIndustry}
        />
      )}

      <HiddenMainIndustryModal hiddenMainIndustry={hiddenMainIndustry} setHiddenMainIndustry={setHiddenMainIndustry} />
      <ConfirmLeavePage shouldBlock={enableSaveOrderBtn}></ConfirmLeavePage>
    </>
  );
};

const StyledTable = styled(Box)`
  border-radius: ${rounded.sm};
  overflow: auto;
  max-height: 575px;
  border: 1px solid ${divider.middle};
  background-color: ${background.white};
  * {
    box-shadow: none;
  }
  .MuiToolbar-dense {
    display: none;
  }
  .MuiTableCell-head {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    position: sticky;
    background-color: white;
    z-index: 500;
    top: 0;
  }
  .Mui-TableHeadCell-Content-Labels {
    > span {
      margin-left: 4px;
      img {
        opacity: 0.2;
      }
      .MuiBadge-badge {
        display: none;
      }
    }
  }
  .MuiTableCell-body {
    font-size: 14px;
    font-weight: 400;
    line-height: 28px;
  }

  // Default
  .MuiTableRow-root {
    background-color: ${background.white};
    box-shadow: none;
    cursor: initial;
    .MuiTableCell-root {
      padding: ${spacing.xxs} ${spacing.sm};
      border-color: ${divider.middle};
      opacity: 1;
    }
    &:not(.MuiTableRow-head):last-child {
      .MuiTableCell-root {
        border-bottom: 0;
      }
    }
  }
  // Hover
  .MuiTableRow-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: ${background.white};
      &::after {
        background-color: ${background.primary};
      }
    }
  }
  // Click/Pressed
  .Mui-selected {
    background-color: ${background.white} !important;
    td::after {
      background: ${background.secondary}!important;
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }
  .MuiPaper-root > .MuiBox-root {
    display: none;
  }

  .MuiCheckbox-root {
    color: ${button.primary}!important;
    height: 24px;
    width: 24px;
    padding: 0;
  }

  .MuiTableCell-root {
    padding: 4px 8px !important;
  }

  // Disabled Row
  .MuiTableCell-root.disabled {
    opacity: 1;
    background: ${background.disable}!important;
    pointer-events: none;
  }

  // added row
  .MuiTableCell-root.added {
    background: ${green[99]}!important;
    &::after {
      background: unset !important;
    }
  }

  // Failed row
  .MuiTableCell-root.failed {
    background: ${background.error}!important;
    &::after {
      background: unset !important;
    }
  }

  // Clickable row
  .MuiTableCell-root.clickable {
    cursor: pointer;
  }
`;
